import { useState } from 'react';
import { getScoreColor, PILLARS } from '../utils/scoring';

// Only hard-skill trainable touchpoints — excludes guest perception items
// (neighborhood, ambiance, lastImpression, memoryAt48hrs, wouldReturn, wouldRecommend are perception-based)
const TRAINABLE_TOUCHPOINTS = {
  'Arrival': ['hostGreeting', 'waitExperience', 'pickupHandoff', 'seatingWalk'],
  'The Setting': ['serverIntro', 'menuPresentation', 'menuAccuracy', 'waterBread', 'familyAccommodation'],
  'Food & Beverage': ['firstBeverage', 'appetizerCourse', 'entreeCourse', 'foodPresentation', 'beverageThroughout', 'barProgram', 'pacing', 'dessertOffer'],
  'Service & Hospitality': ['serviceSpeed', 'attentiveness', 'checkIns', 'teamKnowledge', 'specialRequests', 'managerPresence', 'restroomVisit', 'problemRecovery'],
  'Departure': ['checkPresentation', 'takeawayPackaging', 'coatCheckValet', 'farewell'],
  'Post-Visit': ['followUp'],
};

export default function TrainingCurriculum({ audit, onUpdate }) {
  const [generating, setGenerating] = useState(false);
  const [generatingId, setGeneratingId] = useState(null);
  const [activeModuleId, setActiveModuleId] = useState(null);
  const training = audit.trainingModules || [];
  const scores = audit.scores || {};
  const journey = audit.guestJourney || {};
  const threshold = 5;

  // Find gaps
  const pillarGaps = PILLARS
    .filter(p => (scores[p.id]?.score || 0) > 0 && scores[p.id].score < threshold)
    .map(p => ({ id: `pillar_${p.id}`, type: 'pillar', name: p.name, score: scores[p.id].score, rationale: scores[p.id].rationale || scores[p.id].aiRationale || '', area: 'Business Pillar' }));

  const journeyGaps = [];
  Object.entries(TRAINABLE_TOUCHPOINTS).forEach(([phase, tpIds]) => {
    tpIds.forEach(id => {
      const data = journey[id];
      if (data && data.approved && data.score < threshold) {
        journeyGaps.push({ id: `journey_${id}`, type: 'touchpoint', name: id.replace(/([A-Z])/g, ' $1').replace(/^./, s => s.toUpperCase()), score: data.score, observation: data.observation || data.aiResearch || '', measuredTime: data.measuredTime || null, noiseLevel: data.noiseLevel || null, area: phase });
      }
    });
  });

  const allGaps = [...pillarGaps, ...journeyGaps].sort((a, b) => a.score - b.score);
  const getModule = (gapId) => training.find(m => m.gapId === gapId);
  const generatedModules = training.filter(m => m.title);

  // Active module for tab view
  const activeModule = activeModuleId ? training.find(m => m.gapId === activeModuleId) : null;

  const updateModule = (gapId, field, value) => {
    const updated = training.map(m => m.gapId === gapId ? { ...m, [field]: value } : m);
    onUpdate({ ...audit, trainingModules: updated });
  };

  const updateSection = (gapId, sectionIndex, field, value) => {
    const updated = training.map(m => {
      if (m.gapId !== gapId) return m;
      const sections = [...(m.sections || [])];
      sections[sectionIndex] = { ...sections[sectionIndex], [field]: value };
      return { ...m, sections };
    });
    onUpdate({ ...audit, trainingModules: updated });
  };

  const addSection = (gapId) => {
    const updated = training.map(m => {
      if (m.gapId !== gapId) return m;
      return { ...m, sections: [...(m.sections || []), { heading: '', content: '', activity: '', duration: '5 min' }] };
    });
    onUpdate({ ...audit, trainingModules: updated });
  };

  const removeSection = (gapId, sectionIndex) => {
    const updated = training.map(m => {
      if (m.gapId !== gapId) return m;
      return { ...m, sections: (m.sections || []).filter((_, i) => i !== sectionIndex) };
    });
    onUpdate({ ...audit, trainingModules: updated });
  };

  const updateTakeaway = (gapId, index, value) => {
    const updated = training.map(m => {
      if (m.gapId !== gapId) return m;
      const takeaways = [...(m.keyTakeaways || [])];
      takeaways[index] = value;
      return { ...m, keyTakeaways: takeaways };
    });
    onUpdate({ ...audit, trainingModules: updated });
  };

  const deleteModule = (gapId) => {
    onUpdate({ ...audit, trainingModules: training.filter(m => m.gapId !== gapId) });
    if (activeModuleId === gapId) setActiveModuleId(null);
  };

  const generateModule = async (gap) => {
    setGeneratingId(gap.id);
    setGenerating(true);
    try {
      const apiKey = import.meta.env.VITE_ANTHROPIC_API_KEY;
      const intake = audit.intake || {};
      const prompt = `You are a hospitality training consultant for Next Experience Group. Generate a targeted training module for restaurant/bar staff.

Concept: ${intake.conceptName || 'Unknown'} — ${intake.city || ''}

ISSUE: ${gap.area} > ${gap.name} — Score: ${gap.score}/10
${gap.rationale ? `Rationale: ${gap.rationale}` : ''}${gap.observation ? `\nObservation: ${gap.observation}` : ''}${gap.measuredTime ? `\nMeasured Time: ${gap.measuredTime}` : ''}${gap.noiseLevel ? `\nNoise Level: ${gap.noiseLevel}dB` : ''}

Generate a practical 15-30 minute training module specific to the issue.

Return ONLY this JSON:
{"title":"","duration":"15-30 min","targetAudience":"","objective":"","sections":[{"heading":"","content":"","activity":"","duration":"5 min"}],"keyTakeaways":["","",""],"followUp":""}`;

      const response = await fetch('https://api.anthropic.com/v1/messages', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'x-api-key': apiKey, 'anthropic-version': '2023-06-01', 'anthropic-dangerous-direct-browser-access': 'true' },
        body: JSON.stringify({ model: 'claude-sonnet-4-6', max_tokens: 2000, messages: [{ role: 'user', content: prompt }] }),
      });
      const data = await response.json();
      const text = data.content?.[0]?.text || '';
      const match = text.match(/\{[\s\S]*\}/);
      if (match) {
        const mod = JSON.parse(match[0]);
        const updated = [...training.filter(m => m.gapId !== gap.id), { ...mod, gapId: gap.id, gapName: gap.name, gapScore: gap.score, gapArea: gap.area, generatedAt: new Date().toISOString() }];
        onUpdate({ ...audit, trainingModules: updated });
        setActiveModuleId(gap.id);
      }
    } catch (err) { console.error('Training generation failed:', err); }
    setGenerating(false);
    setGeneratingId(null);
  };

  const generateAll = async () => {
    for (const gap of allGaps) {
      if (!getModule(gap.id)) await generateModule(gap);
    }
  };

  // Export training report as printable HTML
  const exportTraining = () => {
    const conceptName = audit.intake?.conceptName || 'Business';
    const mods = generatedModules;
    const html = `<!DOCTYPE html><html><head><title>Training Curriculum — ${conceptName}</title>
<style>
  body { font-family: Arial, sans-serif; color: #1a1a1a; max-width: 800px; margin: 0 auto; padding: 40px 20px; }
  .header { text-align: center; border-bottom: 2px solid #C9A668; padding-bottom: 20px; margin-bottom: 30px; }
  .header img { height: 40px; margin-bottom: 10px; }
  .header h1 { color: #C9A668; font-size: 24px; margin: 0 0 5px; }
  .header p { color: #888; font-size: 12px; }
  .branding { color: #C9A668; font-size: 11px; text-transform: uppercase; letter-spacing: 2px; margin-top: 5px; }
  .module { page-break-inside: avoid; margin-bottom: 40px; border: 1px solid #eee; border-radius: 8px; overflow: hidden; }
  .module-header { background: #f9f7f2; padding: 15px 20px; border-bottom: 1px solid #eee; }
  .module-header h2 { color: #C9A668; font-size: 18px; margin: 0 0 5px; }
  .module-header .meta { color: #888; font-size: 11px; }
  .module-header .score { display: inline-block; background: ${mods[0]?.gapScore < 3 ? '#fee2e2' : '#fef3c7'}; color: ${mods[0]?.gapScore < 3 ? '#dc2626' : '#d97706'}; padding: 2px 8px; border-radius: 4px; font-weight: bold; font-size: 12px; }
  .module-body { padding: 20px; }
  .objective { background: #f5f5f0; padding: 12px; border-radius: 6px; margin-bottom: 15px; font-size: 13px; }
  .section { margin-bottom: 15px; padding: 12px; background: #fafafa; border-radius: 6px; }
  .section h3 { font-size: 14px; margin: 0 0 8px; }
  .section .duration { color: #888; font-size: 11px; float: right; }
  .section p { font-size: 12px; color: #444; margin: 5px 0; }
  .activity { background: #fdf8ef; border-left: 3px solid #C9A668; padding: 8px 12px; margin-top: 8px; font-size: 12px; }
  .activity strong { color: #C9A668; }
  .takeaways { margin-top: 15px; }
  .takeaways h3 { color: #059669; font-size: 13px; }
  .takeaways li { font-size: 12px; margin-bottom: 4px; }
  .followup { background: #f0fdf4; padding: 10px; border-radius: 6px; font-size: 12px; margin-top: 10px; }
  .footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #eee; color: #aaa; font-size: 11px; }
  @media print { .module { page-break-after: always; } }
</style></head><body>
<div class="header">
  <img src="https://static.wixstatic.com/media/cf458d_d665b41d1e64456698455172873d1399~mv2.png" alt="NEG" />
  <h1>Training Curriculum</h1>
  <p>${conceptName} — ${audit.intake?.city || ''}</p>
  <p class="branding">Training by Next Experience Group</p>
  <p style="color:#888;font-size:11px;margin-top:8px;">${mods.length} modules · Generated ${new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
</div>
${mods.map(m => `
<div class="module">
  <div class="module-header">
    <h2>${m.title}</h2>
    <div class="meta">${m.gapArea} · <span class="score">${m.gapScore?.toFixed(1)}/10</span> · ${m.duration} · ${m.targetAudience}</div>
  </div>
  <div class="module-body">
    <div class="objective"><strong>Objective:</strong> ${m.objective}</div>
    ${(m.sections || []).map(s => `
    <div class="section">
      <span class="duration">${s.duration}</span>
      <h3>${s.heading}</h3>
      <p>${s.content}</p>
      ${s.activity ? `<div class="activity"><strong>Activity:</strong> ${s.activity}</div>` : ''}
    </div>`).join('')}
    ${m.keyTakeaways?.length > 0 ? `<div class="takeaways"><h3>Key Takeaways</h3><ul>${m.keyTakeaways.map(t => `<li>${t}</li>`).join('')}</ul></div>` : ''}
    ${m.followUp ? `<div class="followup"><strong>Measuring Improvement:</strong> ${m.followUp}</div>` : ''}
  </div>
</div>`).join('')}
<div class="footer">Training by Next Experience Group · whatsnext.nextexperience.group</div>
</body></html>`;

    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const w = window.open(url, '_blank');
    if (w) setTimeout(() => { w.print(); }, 500);
  };

  return (
    <div>
      {/* Header with branding */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-2">
        <div>
          <h2 className="font-display text-xl text-white">Training Curriculum</h2>
          <p className="text-[10px] text-brand-gold uppercase tracking-widest mt-0.5">Training by Next Experience Group</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[10px] text-neutral-600">{allGaps.length} gaps · {generatedModules.length} modules</span>
          {generatedModules.length > 0 && (
            <button onClick={exportTraining} className="btn-secondary text-xs py-1.5 px-3">Export Report</button>
          )}
          {allGaps.some(g => !getModule(g.id)) && (
            <button onClick={generateAll} disabled={generating} className="btn-primary text-xs py-1.5 disabled:opacity-50">
              {generating ? 'Generating...' : `Generate All (${allGaps.filter(g => !getModule(g.id)).length})`}
            </button>
          )}
        </div>
      </div>
      <p className="text-xs text-neutral-500 mb-5">AI-generated training modules for items scoring below {threshold}/10. Click any module to edit.</p>

      {allGaps.length === 0 ? (
        <div className="card p-8 text-center">
          <p className="text-neutral-500 text-sm">No items scoring below {threshold}.</p>
          <p className="text-neutral-600 text-xs mt-1">Score pillars and approve guest journey touchpoints to generate training.</p>
        </div>
      ) : (
        <div className="flex flex-col lg:flex-row gap-4">
          {/* Left: topic tabs */}
          <div className="lg:w-72 flex-shrink-0">
            <div className="overflow-x-auto lg:overflow-x-visible scrollbar-hide">
              <div className="flex lg:flex-col gap-1.5 min-w-max lg:min-w-0">
                {allGaps.map(gap => {
                  const module = getModule(gap.id);
                  const sc = getScoreColor(gap.score);
                  const isActive = activeModuleId === gap.id;
                  return (
                    <button
                      key={gap.id}
                      onClick={() => setActiveModuleId(isActive ? null : gap.id)}
                      className={`text-left px-3 py-2.5 rounded-lg transition-all flex items-center gap-2 ${isActive ? 'bg-brand-gold/15 border border-brand-gold/40' : 'bg-neutral-900/50 border border-neutral-800 hover:border-neutral-700'}`}
                    >
                      <span className={`text-xs font-bold ${sc.text} w-7 flex-shrink-0`}>{gap.score.toFixed(1)}</span>
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs font-medium truncate ${isActive ? 'text-brand-gold' : 'text-white'}`}>{gap.name}</p>
                        <p className="text-[9px] text-neutral-600 truncate">{gap.area}</p>
                      </div>
                      {module ? (
                        <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" title="Module generated" />
                      ) : (
                        <span className="w-2 h-2 rounded-full bg-neutral-700 flex-shrink-0" />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Right: module content (editable) */}
          <div className="flex-1">
            {activeModule ? (
              <div className="card p-5">
                {/* Title — editable */}
                <input
                  type="text"
                  value={activeModule.title || ''}
                  onChange={e => updateModule(activeModuleId, 'title', e.target.value)}
                  className="input-field text-base sm:text-sm font-semibold text-brand-gold py-2 mb-3"
                  placeholder="Module title..."
                />

                {/* Meta — editable */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
                  <input type="text" value={activeModule.duration || ''} onChange={e => updateModule(activeModuleId, 'duration', e.target.value)} placeholder="Duration..." className="input-field text-xs py-1.5" />
                  <input type="text" value={activeModule.targetAudience || ''} onChange={e => updateModule(activeModuleId, 'targetAudience', e.target.value)} placeholder="Target audience..." className="input-field text-xs py-1.5" />
                  <div className="text-[9px] text-neutral-600 self-center">{activeModule.gapArea} · {activeModule.gapScore?.toFixed(1)}/10</div>
                </div>

                {/* Objective — editable */}
                <textarea
                  value={activeModule.objective || ''}
                  onChange={e => updateModule(activeModuleId, 'objective', e.target.value)}
                  placeholder="Training objective..."
                  rows={2}
                  className="input-field text-xs mb-4"
                />

                {/* Sections — editable */}
                <div className="space-y-3 mb-4">
                  <div className="flex items-center justify-between">
                    <h4 className="text-xs font-semibold text-white">Sections</h4>
                    <button onClick={() => addSection(activeModuleId)} className="text-[10px] text-brand-gold hover:text-brand-gold/80">+ Add Section</button>
                  </div>
                  {(activeModule.sections || []).map((section, i) => (
                    <div key={i} className="bg-neutral-800/30 rounded-lg p-3 space-y-2">
                      <div className="flex gap-2">
                        <input type="text" value={section.heading || ''} onChange={e => updateSection(activeModuleId, i, 'heading', e.target.value)} placeholder="Section heading..." className="input-field text-xs py-1.5 flex-1 font-semibold" />
                        <input type="text" value={section.duration || ''} onChange={e => updateSection(activeModuleId, i, 'duration', e.target.value)} placeholder="Duration" className="input-field text-xs py-1.5 w-20" />
                        <button onClick={() => removeSection(activeModuleId, i)} className="text-neutral-700 hover:text-red-400 text-xs px-1">✕</button>
                      </div>
                      <textarea value={section.content || ''} onChange={e => updateSection(activeModuleId, i, 'content', e.target.value)} placeholder="Content..." rows={2} className="input-field text-xs" />
                      <div className="flex items-start gap-1">
                        <span className="text-[9px] text-brand-gold mt-1.5 flex-shrink-0">Activity:</span>
                        <textarea value={section.activity || ''} onChange={e => updateSection(activeModuleId, i, 'activity', e.target.value)} placeholder="Hands-on exercise..." rows={1} className="input-field text-xs flex-1" />
                      </div>
                    </div>
                  ))}
                </div>

                {/* Key Takeaways — editable */}
                <div className="mb-4">
                  <h4 className="text-xs font-semibold text-emerald-400 mb-2">Key Takeaways</h4>
                  {(activeModule.keyTakeaways || []).map((t, i) => (
                    <div key={i} className="flex gap-2 mb-1.5">
                      <span className="text-[10px] text-emerald-400/60 mt-2">•</span>
                      <input type="text" value={t} onChange={e => updateTakeaway(activeModuleId, i, e.target.value)} className="input-field text-xs py-1.5 flex-1" />
                    </div>
                  ))}
                </div>

                {/* Follow-up — editable */}
                <div>
                  <h4 className="text-xs font-semibold text-neutral-400 mb-1">Measuring Improvement</h4>
                  <textarea value={activeModule.followUp || ''} onChange={e => updateModule(activeModuleId, 'followUp', e.target.value)} placeholder="How to measure success..." rows={2} className="input-field text-xs" />
                </div>

                {/* Actions */}
                <div className="flex items-center justify-between mt-4 pt-3 border-t border-neutral-800">
                  <button onClick={() => deleteModule(activeModuleId)} className="text-[10px] text-neutral-600 hover:text-red-400 transition-colors">Delete Module</button>
                  <button onClick={() => { const gap = allGaps.find(g => g.id === activeModuleId); if (gap) generateModule(gap); }} disabled={generating} className="text-[10px] px-3 py-1.5 rounded bg-brand-gold/10 text-brand-gold border border-brand-gold/30 hover:bg-brand-gold/20 disabled:opacity-50 transition-colors">
                    {generatingId === activeModuleId ? 'Regenerating...' : 'Regenerate'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="card p-8 text-center">
                <p className="text-neutral-500 text-sm">Select a topic from the left to view or edit its training module.</p>
                <p className="text-neutral-600 text-xs mt-1">Topics without a green dot need training generated.</p>
              </div>
            )}

            {/* No module yet for selected gap */}
            {activeModuleId && !activeModule && (() => {
              const gap = allGaps.find(g => g.id === activeModuleId);
              if (!gap) return null;
              return (
                <div className="card p-8 text-center">
                  <p className="text-neutral-400 text-sm mb-3">{gap.name} — {gap.score.toFixed(1)}/10</p>
                  <button onClick={() => generateModule(gap)} disabled={generating} className="btn-primary text-xs py-2 disabled:opacity-50">
                    {generatingId === gap.id ? 'Generating...' : 'Generate Training Module'}
                  </button>
                </div>
              );
            })()}
          </div>
        </div>
      )}
    </div>
  );
}
